import { useEffect, useRef, useState } from 'react';
import { P } from '@tasca/shared';
import { rest } from '@tasca/client-sdk';
import { useStore } from '@tasca/client-sdk';
import { isDesktop, native } from '../lib/desktop.js';
import { hueOf, initials } from '../lib/avatar.js';
import { has } from '../lib/permissions.js';
import { Icon } from '../ui/Icon.js';
import {
  outputSelectionSupported,
  screenShareSupported,
  type RemoteStream,
  type VoiceClient,
} from '@tasca/client-sdk';

/**
 * Áudio remoto.
 *
 * Um elemento por stream, fora da árvore visível. `autoPlay` sem interacção
 * do utilizador é bloqueado pelos browsers — mas aqui o join JÁ foi uma
 * interacção, logo o gesto está registado e o play passa.
 *
 * O volume é por participante e vem das preferências. `setSinkId` escolhe a
 * saída — só existe em Chromium, daí o selector aparecer condicionalmente.
 */
function RemoteAudio({
  stream,
  volume,
  outputDeviceId,
}: {
  stream: RemoteStream;
  volume: number;
  outputDeviceId: string | null;
}): React.ReactElement {
  const ref = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.srcObject = stream.stream;
    void el.play().catch(() => {
      /* bloqueado: o utilizador terá de clicar outra vez em algo */
    });
    return () => {
      el.srcObject = null;
    };
  }, [stream.stream]);

  useEffect(() => {
    const el = ref.current;
    if (el) el.volume = Math.min(1, Math.max(0, volume / 100));
  }, [volume]);

  useEffect(() => {
    const el = ref.current as (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }) | null;
    if (!el?.setSinkId || !outputDeviceId) return;
    void el.setSinkId(outputDeviceId).catch(() => {
      /* dispositivo desaparecido: fica na saída anterior */
    });
  }, [outputDeviceId]);

  return <audio ref={ref} autoPlay playsInline />;
}

const QUALITY_LABEL = {
  good: 'ligação boa',
  fair: 'ligação instável',
  poor: 'ligação má',
  unknown: 'a medir a ligação…',
} as const;

/**
 * Definições que só existem na app instalada.
 *
 * Vive dentro das definições de voz porque é onde está o push-to-talk — e
 * porque a app ainda não tem um ecrã de preferências geral. Quando tiver,
 * isto muda-se para lá; até lá, estar perto do que configura é melhor que
 * estar num sítio que ninguém abre.
 */
function DesktopSettings(): React.ReactElement | null {
  const [autostart, setAutostart] = useState<boolean | null>(null);

  useEffect(() => {
    if (!native) return;
    void native.autostart
      .isEnabled()
      .then(setAutostart)
      // `null` desactiva a caixa: melhor não deixar mexer num estado que não
      // conseguimos ler do que mostrar um valor inventado.
      .catch(() => setAutostart(null));
  }, []);

  if (!native) return null;

  return (
    <fieldset className="voice-mode desktop-settings">
      <legend>Aplicação</legend>

      <label>
        <input
          type="checkbox"
          checked={autostart === true}
          disabled={autostart === null}
          onChange={(e) => {
            const next = e.target.checked;
            // Optimista, com reversão: o pedido vai ao SO e pode falhar.
            setAutostart(next);
            void native!.autostart.set(next).catch(() => setAutostart(!next));
          }}
        />
        Arrancar com o sistema
      </label>

      <small>Tasca {native.version} · {native.platform}</small>
    </fieldset>
  );
}

/** Painel de definições: dispositivos, modo de transmissão, sensibilidade. */
function VoiceSettingsPanel({ voice }: { voice: VoiceClient }): React.ReactElement {
  const { voiceSettings, voiceDevices } = useStore();
  const [capturing, setCapturing] = useState(false);

  // Capturar a tecla de push-to-talk: um listener temporário, porque o que
  // interessa é o `code` e não há forma de o pedir a um <input>.
  useEffect(() => {
    if (!capturing) return;
    const onKey = (ev: KeyboardEvent): void => {
      ev.preventDefault();
      if (ev.code !== 'Escape') voice.setPushToTalkKey(ev.code);
      setCapturing(false);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [capturing, voice]);

  return (
    <div className="voice-settings">
      <label>
        Microfone
        <select
          value={voiceSettings.inputDeviceId ?? ''}
          onChange={(e) => void voice.setInputDevice(e.target.value || null)}
        >
          <option value="">Predefinido do sistema</option>
          {voiceDevices.inputs.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label}
            </option>
          ))}
        </select>
      </label>

      {outputSelectionSupported() && (
        <label>
          Saída
          <select
            value={voiceSettings.outputDeviceId ?? ''}
            onChange={(e) => voice.setOutputDevice(e.target.value || null)}
          >
            <option value="">Predefinido do sistema</option>
            {voiceDevices.outputs.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {voiceDevices.cameras.length > 0 && (
        <label>
          Câmara
          <select
            value={voiceSettings.cameraDeviceId ?? ''}
            onChange={(e) => void voice.setCameraDevice(e.target.value || null)}
          >
            <option value="">Predefinida do sistema</option>
            {voiceDevices.cameras.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
      )}

      <label>
        Qualidade da câmara
        <select
          value={voiceSettings.videoProfile}
          onChange={(e) =>
            void voice.setVideoProfile(e.target.value as 'low' | 'medium' | 'high')
          }
        >
          <option value="low">320×180 · 15fps</option>
          <option value="medium">640×360 · 24fps</option>
          <option value="high">1280×720 · 30fps</option>
        </select>
        <small>
          É a resolução de CAPTURA. O simulcast envia sempre três camadas
          derivadas dela e cada espectador recebe a que o seu ecrã justifica.
        </small>
      </label>

      <label>
        Partilha de ecrã
        <select
          value={voiceSettings.screenProfile}
          onChange={(e) => voice.setScreenProfile(e.target.value as 'text' | 'motion')}
        >
          <option value="text">Texto — 1080p a 5fps</option>
          <option value="motion">Vídeo — 720p a 30fps</option>
        </select>
        <small>
          Texto mantém uma linha de código legível; vídeo mantém o movimento
          fluido. 1080p a 30fps não cabe no orçamento de banda desta VPS.
        </small>
      </label>

      <fieldset className="voice-mode">
        <legend>Transmissão</legend>
        <label>
          <input
            type="radio"
            name="voice-mode"
            checked={voiceSettings.mode === 'voice'}
            onChange={() => void voice.setMode('voice')}
          />
          Por voz
        </label>
        <label>
          <input
            type="radio"
            name="voice-mode"
            checked={voiceSettings.mode === 'ptt'}
            onChange={() => void voice.setMode('ptt')}
          />
          Premir para falar
        </label>
      </fieldset>

      {voiceSettings.mode === 'voice' ? (
        <label>
          Sensibilidade
          <input
            type="range"
            min={0}
            max={100}
            value={voiceSettings.sensitivity}
            onChange={(e) => voice.setSensitivity(Number(e.target.value))}
          />
          <small>
            Mais alta apanha sussurros — e o ventilador. O DTX do Opus corta o
            envio em silêncio de qualquer forma.
          </small>
        </label>
      ) : (
        <label>
          Tecla
          <button className="key-capture" onClick={() => setCapturing(true)}>
            {capturing ? 'Prime uma tecla…' : voiceSettings.pttKey}
          </button>
          <small>
            {isDesktop
              ? 'Registada no sistema: funciona com a janela em segundo plano. Se outra aplicação já tiver esta tecla, fica a valer só com a janela em foco.'
              : 'Num browser a tecla só funciona com a janela em foco. A app de desktop regista-a no sistema.'}
          </small>
        </label>
      )}

      <DesktopSettings />
    </div>
  );
}

export function VoicePanel({
  voice,
  channelName,
  onLeave,
}: {
  voice: VoiceClient;
  channelName: string | null;
  /** Sair tem de avisar a API, não só fechar o media — ver Chat.tsx. */
  onLeave: () => void;
}): React.ReactElement | null {
  const {
    me,
    voiceState,
    voiceDetail,
    voiceChannelId,
    voiceMuted,
    voiceDeafened,
    voiceServerMute,
    voiceServerDeaf,
    voiceQuality,
    voiceStats,
    voiceSettings,
    voiceStates,
    remoteStreams,
    speaking,
    channels,
    members,
  } = useStore();

  const [showSettings, setShowSettings] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  // Dispositivos novos (auscultadores ligados a meio) devem aparecer na lista
  // sem recarregar a página.
  useEffect(() => {
    const refresh = (): void => void voice.listDevices();
    navigator.mediaDevices?.addEventListener?.('devicechange', refresh);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', refresh);
  }, [voice]);

  if (voiceState === 'idle' || !voiceChannelId) return null;

  // O canal de voz pode não ser o canal activo, nem o guild activo: procurar
  // nos canais de todos os guilds é o que mantém o painel correcto depois de
  // trocar de servidor sem sair da voz.
  let guildId: string | null = null;
  let channelPermissions: string | undefined;
  let voiceChannelsOfGuild: { id: string; name: string | null }[] = [];
  for (const [gid, list] of channels) {
    const found = list.find((c) => c.id === voiceChannelId);
    if (!found) continue;
    guildId = gid;
    channelPermissions = found.permissions;
    voiceChannelsOfGuild = list.filter((c) => c.type === 1 && c.id !== voiceChannelId);
    break;
  }

  const canMute = has(channelPermissions, P.MUTE_MEMBERS);
  const canDeafen = has(channelPermissions, P.DEAFEN_MEMBERS);
  const canMove = has(channelPermissions, P.MOVE_MEMBERS);
  // VIDEO e STREAM são verificadas no SFU no `produce`; aqui só evitamos
  // oferecer um botão que o servidor vai recusar.
  const canVideo = has(channelPermissions, P.VIDEO);
  const canStream = has(channelPermissions, P.STREAM);

  // O vídeo não vive aqui: numa coluna de 232px uma câmara é um selo. Vai
  // para o palco, em VoiceStage.tsx. Aqui fica o comando, que tem de estar
  // visível mesmo depois de navegar para outro canal.
  const audioStreams = remoteStreams.filter((s) => s.kind === 'audio');

  // Participantes do canal onde estou, vindos do gateway (não do SFU): é a
  // lista autoritativa, e aparece mesmo antes de o media ligar.
  const participants = [...voiceStates.values()].filter(
    (v) => v.channelId === voiceChannelId,
  );

  const memberList = guildId ? (members.get(guildId) ?? []) : [];
  const nameOf = (userId: string): string => {
    if (userId === me?.id) return me?.displayName ?? 'Eu';
    const member = memberList.find((m) => m.user.id === userId);
    if (member) return member.nick ?? member.user.displayName;
    return remoteStreams.find((s) => s.peerId === userId)?.displayName ?? 'Utilizador';
  };

  const label =
    voiceState === 'connected'
      ? 'ligado'
      : voiceState === 'connecting'
        ? 'a ligar media…'
        : voiceState === 'signalling'
          ? 'a negociar…'
          : 'falhou';

  async function moderate(
    userId: string,
    patch: { serverMute?: boolean; serverDeaf?: boolean; channelId?: string | null },
  ): Promise<void> {
    if (!guildId) return;
    setOpenMenu(null);
    try {
      await rest.moderateVoice(guildId, userId, patch);
    } catch (err) {
      // A hierarquia dos grupos é a razão habitual; dizer qual poupa uma
      // viagem aos logs.
      useStore
        .getState()
        .setVoiceState(
          voiceState,
          err instanceof Error ? `moderação recusada: ${err.message}` : 'moderação recusada',
        );
    }
  }

  return (
    <section className="voice" aria-label="Sessão de voz">
      {audioStreams.map((s) => (
        <RemoteAudio
          key={s.consumerId}
          stream={s}
          volume={voiceSettings.volumes[s.peerId] ?? 100}
          outputDeviceId={voiceSettings.outputDeviceId}
        />
      ))}

      <header className="voice-head">
        <span className={`voice-dot ${voiceState}`} />
        <strong>{channelName ?? 'Voz'}</strong>
        <span className="voice-status">{label}</span>
        {voiceState === 'connected' && (
          <span
            className={`voice-quality ${voiceQuality}`}
            title={
              voiceStats?.rtt !== undefined
                ? `${QUALITY_LABEL[voiceQuality]} · ${Math.round(voiceStats.rtt)}ms`
                : QUALITY_LABEL[voiceQuality]
            }
            aria-label={QUALITY_LABEL[voiceQuality]}
          >
            <i />
            <i />
            <i />
          </span>
        )}
      </header>

      {voiceState === 'failed' && voiceDetail && (
        <p className="voice-error">
          {voiceDetail}
          <br />
          <small>
            Se isto persistir, é quase sempre a gama UDP bloqueada ou o
            announcedIp errado — ver docs/05.
          </small>
        </p>
      )}

      {(voiceServerMute || voiceServerDeaf) && (
        <p className="voice-moderated">
          {voiceServerMute && voiceServerDeaf
            ? 'Um moderador silenciou-te e ensurdeceu-te.'
            : voiceServerMute
              ? 'Um moderador silenciou-te neste canal.'
              : 'Um moderador ensurdeceu-te neste canal.'}
        </p>
      )}

      <ul className="voice-peers">
        {participants.map((p) => {
          const isMe = p.userId === me?.id;
          const name = nameOf(p.userId);
          const isSpeaking = speaking.has(isMe ? 'self' : p.userId);
          const muted = p.selfMute || p.serverMute;
          const locallyMuted = !isMe && voice.isPeerMuted(p.userId);
          const volume = voiceSettings.volumes[p.userId] ?? 100;
          const menuOpen = openMenu === p.userId;

          return (
            <li key={p.userId} className={isSpeaking && !muted ? 'speaking' : ''}>
              <button
                className="voice-peer"
                onClick={() => setOpenMenu(menuOpen ? null : p.userId)}
                aria-expanded={menuOpen}
              >
                <span className="avatar" style={{ ['--hue' as string]: hueOf(p.userId) }}>
                  {initials(name)}
                </span>
                <span className="voice-name">{name}</span>
                {/* A cor diz a origem da decisão: moderada em âmbar, própria em
                    vermelho — ver `.voice-flag.mod`. */}
                {p.serverMute && (
                  <span className="voice-flag mod" title="silenciado por moderação">
                    <Icon name="micOff" />
                  </span>
                )}
                {muted && !p.serverMute && (
                  <span className="voice-flag" title="sem microfone">
                    <Icon name="micOff" />
                  </span>
                )}
                {(p.selfDeaf || p.serverDeaf) && (
                  <span className="voice-flag" title="sem áudio">
                    <Icon name="headphonesOff" />
                  </span>
                )}
                {locallyMuted && (
                  <span className="voice-flag" title="silenciado só para mim">
                    <Icon name="volumeX" />
                  </span>
                )}
                {p.selfStream && (
                  <span className="voice-flag" title="a partilhar ecrã">
                    <Icon name="screenShare" />
                  </span>
                )}
              </button>

              {menuOpen && (
                <div className="voice-menu" role="menu">
                  {!isMe && (
                    <>
                      <label className="voice-volume">
                        Volume {volume}%
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={volume}
                          onChange={(e) => voice.setPeerVolume(p.userId, Number(e.target.value))}
                        />
                      </label>
                      <button
                        onClick={() => {
                          voice.setPeerMuted(p.userId, !locallyMuted);
                          setOpenMenu(null);
                        }}
                      >
                        {locallyMuted ? 'Ouvir outra vez' : 'Silenciar só para mim'}
                      </button>
                    </>
                  )}

                  {!isMe && (canMute || canDeafen || canMove) && <hr />}

                  {!isMe && canMute && (
                    <button
                      onClick={() => void moderate(p.userId, { serverMute: !p.serverMute })}
                    >
                      {p.serverMute ? 'Levantar silêncio do servidor' : 'Silenciar no servidor'}
                    </button>
                  )}
                  {!isMe && canDeafen && (
                    <button
                      onClick={() => void moderate(p.userId, { serverDeaf: !p.serverDeaf })}
                    >
                      {p.serverDeaf ? 'Devolver o áudio' : 'Ensurdecer no servidor'}
                    </button>
                  )}
                  {!isMe && canMove && voiceChannelsOfGuild.length > 0 && (
                    <label className="voice-move">
                      Mover para
                      <select
                        defaultValue=""
                        onChange={(e) => {
                          if (e.target.value) {
                            void moderate(p.userId, { channelId: e.target.value });
                          }
                        }}
                      >
                        <option value="">Escolher canal…</option>
                        {voiceChannelsOfGuild.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name ?? c.id}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {!isMe && canMove && (
                    <button
                      className="danger"
                      onClick={() => void moderate(p.userId, { channelId: null })}
                    >
                      Desligar da voz
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="voice-controls">
        <button
          className={voiceMuted ? 'on' : ''}
          disabled={voiceServerMute}
          onClick={() => void voice.setMuted(!voiceMuted)}
          title={
            voiceServerMute
              ? 'Silenciado por um moderador'
              : voiceSettings.mode === 'ptt'
                ? `Premir para falar: ${voiceSettings.pttKey}`
                : voiceMuted
                  ? 'Activar microfone'
                  : 'Silenciar microfone'
          }
        >
          <Icon name={voiceMuted ? 'micOff' : 'mic'} />
        </button>

        <button
          className={voiceDeafened ? 'on' : ''}
          onClick={() => void voice.setDeafened(!voiceDeafened)}
          title={voiceDeafened ? 'Voltar a ouvir' : 'Ensurdecer (deixa de receber áudio)'}
        >
          <Icon name={voiceDeafened ? 'headphonesOff' : 'headphones'} />
        </button>

        <button
          className={voice.screenOn ? 'on' : ''}
          disabled={!canStream || !screenShareSupported()}
          onClick={() =>
            void (voice.screenOn ? voice.stopScreenShare() : voice.startScreenShare()).catch(
              () => {},
            )
          }
          title={
            !canStream
              ? 'Sem permissão para partilhar ecrã neste canal'
              : !screenShareSupported()
                ? 'Este browser não permite partilhar o ecrã'
                : voice.screenOn
                  ? 'Parar de partilhar'
                  : `Partilhar ecrã (${voiceSettings.screenProfile === 'text' ? 'texto' : 'vídeo'})`
          }
        >
          <Icon name="screenShare" />
        </button>

        <button
          className={voice.cameraOn ? 'on' : ''}
          disabled={!canVideo}
          onClick={() =>
            void (voice.cameraOn ? voice.stopCamera() : voice.startCamera()).catch(() => {})
          }
          title={
            canVideo
              ? voice.cameraOn
                ? 'Desligar câmara'
                : 'Ligar câmara'
              : 'Sem permissão para vídeo neste canal'
          }
        >
          <Icon name="video" />
        </button>

        <button
          className={showSettings ? 'on' : ''}
          onClick={() => setShowSettings(!showSettings)}
          title="Definições de voz"
          aria-expanded={showSettings}
        >
          <Icon name="settings" />
        </button>

        <button className="leave" title="Sair da voz" onClick={onLeave}>
          <Icon name="phoneOff" />
        </button>
      </div>

      {showSettings && <VoiceSettingsPanel voice={voice} />}
    </section>
  );
}
